/**
 * 학원 운영 — 원장용. 학생 추가 + 오늘 출석 체크.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoArrowBack } from 'react-icons/io5';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const AcademyManagePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const date = todayStr();

  const [academy, setAcademy] = useState(null);
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const [a, s, att] = await Promise.all([
      academyService.getAcademy(id),
      academyService.getStudents(id),
      academyService.getAttendance(id, date),
    ]);
    setAcademy(a);
    setStudents(s);
    setRecords(att.records || {});
    setLoading(false);
  }, [id, date]);

  useEffect(() => { load(); }, [load]);

  const addStudent = async () => {
    if (!newName.trim()) return;
    const stu = await academyService.addStudent(id, newName.trim());
    setStudents((prev) => [...prev, stu]);
    setNewName('');
  };

  const toggle = (sid) => {
    setRecords((prev) => ({
      ...prev,
      [sid]: prev[sid] === 'present' ? 'absent' : 'present',
    }));
    setSaved(false);
  };

  const save = async () => {
    await academyService.saveAttendance(id, date, records);
    setSaved(true);
  };

  if (loading) return <Center><BrandLoader /></Center>;

  return (
    <Container>
      <TopBar>
        <BackBtn onClick={() => navigate('/app/my')}><IoArrowBack size={22} /></BackBtn>
        <TopTitle>{academy?.name}</TopTitle>
      </TopBar>

      <Body>
        <SectionHead>학생 {students.length}명</SectionHead>
        <AddRow>
          <AddInput
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addStudent(); }}
            placeholder="학생 이름"
          />
          <AddBtn type="button" onClick={addStudent}>추가</AddBtn>
        </AddRow>

        <SectionHead>오늘 출석 · {date}</SectionHead>
        {students.length === 0 ? (
          <Hint>학생을 먼저 추가하세요.</Hint>
        ) : (
          <List>
            {students.map((s) => (
              <Row key={s.id}>
                <RowName>{s.name}</RowName>
                <StateBtn type="button" $state={records[s.id]} onClick={() => toggle(s.id)}>
                  {records[s.id] === 'present' ? '출석'
                    : records[s.id] === 'absent' ? '결석' : '체크'}
                </StateBtn>
              </Row>
            ))}
          </List>
        )}

        {students.length > 0 && (
          <SaveBtn type="button" onClick={save}>
            {saved ? '저장됨 ✓' : '출석 저장'}
          </SaveBtn>
        )}
      </Body>
    </Container>
  );
};

export default AcademyManagePage;

const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background.default};
`;

const TopBar = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const BackBtn = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TopTitle = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const Body = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 18px 16px 40px;
`;

const SectionHead = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 18px 0 10px;
`;

const AddRow = styled.div`
  display: flex;
  gap: 8px;
`;

const AddInput = styled.input`
  flex: 1;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 15px;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; outline: none; }
`;

const AddBtn = styled.button`
  flex-shrink: 0;
  padding: 0 18px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
`;

const RowName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StateBtn = styled.button`
  padding: 7px 16px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $state }) => ($state ? '#fff' : '#6B7280')};
  background: ${({ $state, theme }) =>
    $state === 'present' ? theme.colors.primary
      : $state === 'absent' ? theme.colors.accent.red
        : '#F0F1F4'};
`;

const SaveBtn = styled.button`
  width: 100%;
  margin-top: 22px;
  padding: 15px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
`;

const Hint = styled.p`
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 28px 0;
`;

const Center = styled.p`
  text-align: center;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.text.hint};
`;
